import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from "@mui/material/CircularProgress";
import { Grid, InputLabel, Typography } from "@mui/material";
import { toast } from "../../services/toast";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import SelectConection from "../SelectConection";
import toastError from "../../errors/toastError";

const ForwardMessageModal = ({
  message,
  sourceWhatsappId,
  modalOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [connectionId, setConnectionId] = useState(sourceWhatsappId || "");

  useEffect(() => {
    if (modalOpen) {
      setConnectionId(sourceWhatsappId || "");
    }
  }, [modalOpen, sourceWhatsappId]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedContact(null);
    setOptions([]);
  };

  const handleForward = async () => {
    if (!selectedContact || !message?.id) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${message.id}/forward`, {
        contactId: selectedContact.id,
        whatsappId: connectionId || undefined,
      });

      if (data?.delivered === false && data?.reason === "window_closed") {
        toast.warn(
          "No se pudo reenviar: la ventana de 24h con este contacto está cerrada. Se abrió el ticket para que le envíes una plantilla."
        );
      } else {
        toast.success("Mensaje reenviado.");
      }

      if (data?.ticket?.uuid) {
        navigate(`/tickets/${data.ticket.uuid}`);
      }

      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const renderOptionLabel = (option) =>
    option.number ? `${option.name} - ${option.number}` : `${option.name}`;

  return (
    <Dialog open={modalOpen} onClose={handleClose}>
      <DialogTitle id="forward-dialog-title">Reenviar mensaje</DialogTitle>
      <DialogContent dividers>
        <Grid style={{ width: 300 }} container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              fullWidth
              size="small"
              options={options}
              loading={loading}
              clearOnBlur
              autoHighlight
              clearOnEscape
              getOptionLabel={renderOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, newValue) => setSelectedContact(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar contacto destino"
                  variant="outlined"
                  size="small"
                  margin="dense"
                  autoFocus
                  onChange={(e) => setSearchParam(e.target.value)}
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps.input.endAdornment}
                        </React.Fragment>
                      ),
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <InputLabel shrink>Enviar desde la conexión</InputLabel>
            <SelectConection
              value={connectionId}
              onChange={(value) => setConnectionId(value)}
            />
            <Typography variant="caption" color="textSecondary">
              Por defecto sale por la conexión de este chat.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={sending}
          variant="outlined"
        >
          {i18n.t("newTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="button"
          disabled={!selectedContact}
          onClick={handleForward}
          color="primary"
          loading={sending}
        >
          Reenviar
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardMessageModal;
